// ======================================================================
// 📁 File: EquipmentMasterPage.jsx
// 📝 Purpose:
//   Equipment Master – List Page
//   - Gradient Header
//   - KPI Bar
//   - Filters
//   - Equipment Table
//   - Drawer-based Create / Edit
//   - View Details Drawer
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";

import {
  getEquipmentList,
} from "./services/equipmentService";

import EquipmentKpiBar from "./EquipmentKpiBar";
import EquipmentDrawer from "./EquipmentDrawer";
import EquipmentDetailsDrawer from "./EquipmentDetailsDrawer";

import axiosClient from "../../../../../api/axiosClient";

export default function EquipmentMasterPage() {

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [families, setFamilies] = useState([]);
  const [variants, setVariants] = useState([]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  const [familyId, setFamilyId] = useState("");
  const [variantId, setVariantId] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");

  const [editingRow, setEditingRow] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  // ==========================================================
  // Load Product Families
  // ==========================================================
  const loadFamilies = useCallback(async () => {

    try {

      const res = await axiosClient.get(
        "/api/ppe/masters/product-families"
      );

      setFamilies(res.data?.data || []);

    } catch (err) {

      console.error(err);

    }

  }, []);

  // ==========================================================
  // Load Variants by Family
  // ==========================================================
  const loadVariants = useCallback(async (family) => {

    try {

      if (!family) {
        setVariants([]);
        return;
      }

      const res = await axiosClient.get(
        "/api/ppe/masters/product-variants",
        {
          params: {
            product_family_id: family,
            is_active: 1
          }
        }
      );

      setVariants(res.data?.data || []);

    } catch (err) {

      console.error(err);

    }

  }, []);

  // ==========================================================
  // Load Equipment
  // ==========================================================
  const loadEquipment = useCallback(async () => {

    try {

      setLoading(true);

      const res = await getEquipmentList({
        search,
        product_variant_id:
          variantId || null,

        is_active:
          status === "ALL"
            ? null
            : status === "ACTIVE"
            ? true
            : false
      });

      setRows(res.data || []);

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);

    }

  }, [
    search,
    status,
    variantId
  ]);

  // ==========================================================
  // Initial Load
  // ==========================================================
  useEffect(() => {

    loadFamilies();

  }, [loadFamilies]);

  useEffect(() => {

    loadEquipment();

  }, [loadEquipment]);

  useEffect(() => {

    loadVariants(familyId);

  }, [
    familyId,
    loadVariants
  ]);

  // ==========================================================
  // Drawer Handlers
  // ==========================================================
  const openCreate = () => {

    setEditingRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);

  };

  const openEdit = (row) => {

    setEditingRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);

  };

  const openView = (row) => {

    setSelectedEquipment(row);
    setDetailsOpen(true);

  };

  // ==========================================================
  // Render
  // ==========================================================
  return (
    <div>

      {/* ==================================================== */}
      {/* Header */}
      {/* ==================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg,#4f46e5 0%,#6366f1 50%,#7c3aed 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >

        <div>

          <h4 style={{ margin: 0 }}>
            Equipment Master
          </h4>

          <div
            style={{
              fontSize: 13,
              opacity: 0.9
            }}
          >
            Engineering Equipment Configuration
          </div>

        </div>

        <button
          className="btn btn-light"
          onClick={openCreate}
        >
          + Create Equipment
        </button>

      </div>

      {/* ==================================================== */}
      {/* KPI */}
      {/* ==================================================== */}
      <EquipmentKpiBar rows={rows} />

      {/* ==================================================== */}
      {/* Filters */}
      {/* ==================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16
        }}
      >

        <input
          className="form-control"
          placeholder="Search Equipment"
          style={{ maxWidth: 240 }}
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={familyId}
          onChange={(e) =>
            setFamilyId(e.target.value)
          }
        >
          <option value="">
            All Product Families
          </option>

          {families.map((f) => (
            <option
              key={f.id}
              value={f.id}
            >
              {f.name}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={variantId}
          onChange={(e) =>
            setVariantId(e.target.value)
          }
        >
          <option value="">
            All Product Variants
          </option>

          {variants.map((v) => (
            <option
              key={v.id}
              value={v.id}
            >
              {v.name}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 180 }}
          value={status}
          onChange={(e) =>
            setStatus(e.target.value)
          }
        >
          <option value="ALL">
            All Status
          </option>

          <option value="ACTIVE">
            Active
          </option>

          <option value="INACTIVE">
            Inactive
          </option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadEquipment}
        >
          Apply
        </button>

      </div>

      {/* ==================================================== */}
      {/* Table */}
      {/* ==================================================== */}
      <div className="card">

        <div className="card-body p-0">

          <table className="table table-bordered mb-0">

            <thead className="table-light">
              <tr>
                <th>Equipment Code</th>
                <th>Equipment Name</th>
                <th>Category</th>
                <th>Product Family</th>
                <th>Product Variant</th>
                <th>Status</th>
                <th style={{ width: 180 }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>

              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center"
                  >
                    Loading...
                  </td>
                </tr>
              )}

              {!loading &&
                rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-muted"
                  >
                    No equipment found
                  </td>
                </tr>
              )}

              {rows.map((r) => (

                <tr key={r.id}>

                  <td>{r.equipment_code}</td>

                  <td>{r.equipment_name}</td>

                  <td>
                    {r.equipment_category || "-"}
                  </td>

                  <td>
                    {r.product_family_name || "-"}
                  </td>

                  <td>
                    {r.product_variant_name || "-"}
                  </td>

                  <td>
                    <span
                      className={`badge ${
                        r.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_active
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </td>

                  <td>

                    <div
                      style={{
                        display: "flex",
                        gap: 6
                      }}
                    >

                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() =>
                          openView(r)
                        }
                      >
                        View
                      </button>

                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          openEdit(r)
                        }
                      >
                        Edit
                      </button>

                    </div>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* ==================================================== */}
      {/* Create/Edit Drawer */}
      {/* ==================================================== */}
      <EquipmentDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={editingRow}
        onClose={() =>
          setDrawerOpen(false)
        }
        onSaved={loadEquipment}
      />

      {/* ==================================================== */}
      {/* Details Drawer */}
      {/* ==================================================== */}
      <EquipmentDetailsDrawer
        open={detailsOpen}
        equipment={selectedEquipment}
        onClose={() =>
          setDetailsOpen(false)
        }
      />

    </div>
  );
}